import { useEffect } from 'react';
import type { Scalars } from '@frameio/graphql-client/generated';
import { configureSegmentOt } from '@frameio/segment-ot';
import type { IdentifyOptions } from '@frameio/telemetry';
import { identify as telemetryIdentify } from '@frameio/telemetry';
import { isServer } from '@frameio/utils/config';
import { trackPageView } from '../utils';
import { useCoreOptionsForAnalytics } from './useCoreOptionsForAnalytics';
import { useIdentify } from './useIdentify';
import { useTrackLanguagePreferenceIfCachedTraitChanged } from './useTrackLanguagePreferenceIfCachedTraitChanged';
import { useUpdateGdprConsent } from './useUpdateGdprConsent';

export type MeForAnalytics = {
  id: Scalars['ID']['output'] | null;
  email?: string;
  intercomHash?: string;
  isEdu?: boolean;
};

type AnalyticsArgs<T extends MeForAnalytics> = {
  me: T;
  pageViewIdentifier: Parameters<typeof trackPageView>[0] | null;
  options?: IdentifyOptions;
};

/**
 * Generic function that fires Segment `identify` and `page` calls in the app.
 * Takes an object containing the current user (`me` query) and a
 * `pageViewIdentifier` in order to fire our `identify` and `page` calls.
 * @param {Object} data - An object containing the current user (`me` query) and
 * a `pageViewIdentifier`
 *
 * Example use:
 * ```
 * useAnalytics({ me, pageViewIdentifier })
 * ```
 */
export const useAnalytics = ({
  me,
  pageViewIdentifier,
  options,
}: AnalyticsArgs<MeForAnalytics>): void => {
  useEffect(() => {
    if (me?.id) {
      telemetryIdentify(
        {
          id: me.id,
          email: me.email || undefined,
          accountId: options?.identifyOptions?.accountId,
        },
        { buildInfo: options?.buildInfo }
      );
    }
  }, [
    me?.id,
    me?.email,
    options?.identifyOptions?.accountId,
    options?.buildInfo,
  ]);

  useEffect(() => {
    if (!isServer) {
      configureSegmentOt({ isEdu: me?.isEdu });
    }
  }, [me?.isEdu]);

  const { coreOptions } = useCoreOptionsForAnalytics(me?.isEdu);
  const version = options?.buildInfo?.version;
  const context = version
    ? { ...coreOptions()?.context, version }
    : coreOptions()?.context;

  useEffect(
    () => trackPageView(pageViewIdentifier, context),

    /** Exclude `context` from deps array since we only want to track when
     * `pageViewIdentifier` changes  */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pageViewIdentifier]
  );

  useTrackLanguagePreferenceIfCachedTraitChanged(me?.id);
  useIdentify({
    options: {
      integrations: {
        ...(typeof window !== 'undefined' && 'integrations' in window
          ? (window.integrations as Record<string, unknown>)
          : {}),
        Intercom: {
          user_hash: me?.intercomHash,
          user_id: me?.id,
        },
        // Apply any integration overrides passed via options
        ...options?.integrations,
      },
      context,
    },
  });

  useUpdateGdprConsent(me);
};
